import { useState, useMemo, useEffect } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { X, Eye, Pencil, Code2, Save } from "lucide-react";
import { Panel, SelectInput, Field } from "@/components/cms/kit";

export const STATUS_OPTIONS = [
  { value: "draft", label: "Draft — not visible on the site" },
  { value: "published", label: "Published — live on the site" },
  { value: "inactive", label: "Inactive — hidden but preserved" },
];
export const DEAL_STATUS_OPTIONS = [
  ...STATUS_OPTIONS,
  { value: "expired", label: "Expired — kept but shown as expired" },
];

export function PublishingPanel({ status, onChange, options = STATUS_OPTIONS, extra }) {
  return (
    <Panel title="Publishing" defaultOpen testid="publishing-panel">
      <Field label="Status" testid="status-field">
        <SelectInput value={status || "draft"} onChange={onChange} options={options} testid="status-select" data-testid="status-select" />
      </Field>
      {extra}
    </Panel>
  );
}

const btnLabel = (status) =>
  status === "published" ? "Save & Publish" : status === "inactive" ? "Save (Inactive)" : status === "expired" ? "Save (Expired)" : "Save as Draft";

export function FormShell({ coll, title, isNew, dirty, status, getPayload, validate, children, preview, onClose, onSaved }) {
  const [tab, setTab] = useState("form");
  const [saving, setSaving] = useState(false);
  const [json, setJson] = useState("");

  const close = () => {
    if (dirty && !window.confirm("You have unsaved changes. Discard them and close?")) return;
    onClose();
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }); // eslint-disable-line

  const doSave = async (payloadOverride) => {
    let payload = payloadOverride || getPayload();
    const err = validate ? validate(payload) : null;
    if (err) return toast.error(err);
    delete payload.__new;
    setSaving(true);
    // The upsert is idempotent, so we can safely auto-retry transient origin/proxy
    // hiccups (Cloudflare 5xx / dropped connection) instead of failing the publish.
    let lastErr;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        await api.adminUpsert(coll, payload);
        toast.success(isNew ? "Created" : "Saved");
        onSaved();
        setSaving(false);
        return;
      } catch (e) {
        lastErr = e;
        const st = e.response?.status;
        const transient = !e.response || (st >= 500 && st < 600);
        if (!transient) break;
        if (attempt < 2) await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
      }
    }
    const st = lastErr?.response?.status;
    const detail = lastErr?.response?.data?.detail;
    const msg = typeof detail === "string" ? detail
      : (!lastErr?.response || (st >= 500 && st < 600))
        ? "The server had a temporary hiccup and couldn't confirm the save. Please click Save again — your article may already be saved."
        : "Save failed";
    toast.error(msg);
    setSaving(false);
  };

  const switchTab = (t) => {
    if (t === "json") setJson(JSON.stringify(getPayload(), null, 2));
    setTab(t);
  };

  const saveJson = () => {
    let parsed;
    try { parsed = JSON.parse(json); } catch { return toast.error("Invalid JSON"); }
    doSave(parsed);
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4" onClick={close}>
      <div className="w-full max-w-4xl h-[92vh] card-surface rounded-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-3 min-w-0">
            <span className="font-display font-bold text-white truncate">{title}</span>
            {!isNew && <span className="text-[11px] font-mono text-slate-500 hidden sm:inline">{status}</span>}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => switchTab("form")} data-testid="tab-form" className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs ${tab === "form" ? "bg-white/10 text-white" : "text-slate-400 hover:text-white"}`}><Pencil size={13} /> <span className="hidden sm:inline">Editor</span></button>
            <button onClick={() => switchTab("preview")} data-testid="tab-preview" className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs ${tab === "preview" ? "bg-white/10 text-white" : "text-slate-400 hover:text-white"}`}><Eye size={13} /> <span className="hidden sm:inline">Preview</span></button>
            <button onClick={() => switchTab("json")} data-testid="tab-json" className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs ${tab === "json" ? "bg-white/10 text-white" : "text-slate-400 hover:text-white"}`}><Code2 size={13} /> <span className="hidden sm:inline">Advanced</span></button>
            <button onClick={close} className="ml-1 text-slate-400 hover:text-white p-1"><X size={18} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {tab === "form" && <div className="p-4 sm:p-5 space-y-4">{children}</div>}
          {tab === "preview" && (
            <div className="bg-klyroo-bg min-h-full">
              <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="preview-body">{preview}</div>
            </div>
          )}
          {tab === "json" && (
            <div className="p-4 h-full flex flex-col">
              <p className="text-xs font-mono text-slate-500 mb-2">Advanced: raw document. Saving here writes exactly what you enter.</p>
              <textarea value={json} onChange={(e) => setJson(e.target.value)} spellCheck={false} data-testid="advanced-json"
                className="flex-1 min-h-[400px] bg-klyroo-bg border border-white/10 rounded-xl p-4 font-mono text-xs text-slate-200 outline-none focus:border-klyroo-cyan/50 resize-none" />
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t border-white/10">
          <button onClick={close} className="px-4 py-2 rounded-lg text-slate-300 hover:bg-white/5 text-sm">Cancel</button>
          {tab === "json" ? (
            <button onClick={saveJson} disabled={saving} data-testid="admin-save" className="inline-flex items-center gap-1.5 px-5 py-2 rounded-lg bg-klyroo-cyan text-black font-semibold text-sm hover:bg-white disabled:opacity-60"><Save size={15} /> {saving ? "Saving…" : "Save JSON"}</button>
          ) : (
            <button onClick={() => doSave()} disabled={saving} data-testid="admin-save" className="inline-flex items-center gap-1.5 px-5 py-2 rounded-lg bg-klyroo-cyan text-black font-semibold text-sm hover:bg-white disabled:opacity-60"><Save size={15} /> {saving ? "Saving…" : btnLabel(status)}</button>
          )}
        </div>
      </div>
    </div>
  );
}

// small hook: track a form object + dirtiness against its initial snapshot
export function useForm(initial) {
  const [f, setF] = useState(initial);
  const snapshot = useMemo(() => JSON.stringify(initial), []); // eslint-disable-line
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const dirty = JSON.stringify(f) !== snapshot;
  return { f, setF, set, dirty };
}
